/**
 * Files the person hands Mochi in a creation chat or the Studio.
 *
 * Mochi reads attachments by **Drive item id** (`file_ids` / `source_file_ids`
 * → the assistant's `file_for_model`, which opens each file as the person).
 * The Hub's chat upload (`POST /api/assistant/attachments/`) answers with a
 * short-lived cache id instead, so it can't feed these endpoints. A new file
 * is therefore uploaded straight to the person's own Drive — into an
 * "Attachments for Mochi" folder, as the spec describes — and a file already
 * in Files is attached by its id.
 */
import { driveService, type DriveItem } from "../../../modules/vault/api";

export const MAX_ATTACHMENTS = 8;
export const MAX_ATTACHMENT_BYTES = 30 * 1024 * 1024;
export const ATTACH_FOLDER = "Attachments for Mochi";
export const ATTACH_ACCEPT =
  "image/png,image/jpeg,image/gif,image/webp,application/pdf,.pdf,.docx,.pptx,.xlsx,.csv,.txt,.md";

const EXTENSIONS = new Set(["png", "jpg", "jpeg", "gif", "webp", "pdf", "docx", "pptx", "xlsx", "csv", "txt", "md"]);

export interface Attachment {
  /** Local key (stable while uploading). */
  key: string;
  name: string;
  size: number;
  /** Drive item id once uploaded / picked. */
  fileId: string | null;
  status: "uploading" | "ready" | "error";
  error?: string;
  /** Object URL for an image thumbnail (revoked on removal). */
  preview?: string | null;
}

export function isAcceptedFile(file: File): boolean {
  const ext = file.name.includes(".") ? file.name.split(".").pop()!.toLowerCase() : "";
  return EXTENSIONS.has(ext) || /^image\/(png|jpeg|gif|webp)$/.test(file.type);
}

let folderPromise: Promise<string | null> | null = null;

/** The person's "Attachments for Mochi" folder, found or made once per page. */
export function attachmentsFolder(): Promise<string | null> {
  if (!folderPromise) {
    folderPromise = (async () => {
      try {
        const root = await driveService.getItems({ page_size: 200, type: "folder" });
        const hit = root.results.find((i) => i.type === "folder" && i.name === ATTACH_FOLDER);
        if (hit) return hit.id;
        const made = await driveService.createFolder({ name: ATTACH_FOLDER });
        return made.id;
      } catch {
        // No folder (e.g. listing refused): fall back to the Drive root.
        return null;
      }
    })();
  }
  return folderPromise;
}

/** Test hook. */
export function resetAttachmentsFolder() {
  folderPromise = null;
}

export async function uploadAttachment(file: File): Promise<DriveItem> {
  const parent = await attachmentsFolder();
  return driveService.uploadFile(file, parent);
}

export const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
